import {
  FETCH_PRODUCTS, ADD_TO_CART, REMOVE_FROM_CART,
  SEARCH_PRODUCTS, SHOW_SEARCH, CHANGE_CATEGORY,
  CLEAR_CART, TOGGLE_FAVORITE, UPDATE_CART_ITEM
} from '../actions/types';

/* ─────────────────────────────
   SAFE STORAGE HELPERS
───────────────────────────── */
const safeJSON = (val, fallback) => {
  try {
    return val ? JSON.parse(val) : fallback;
  } catch (e) {
    return fallback;
  }
};

const safeLSGet = (key, fallback) => {
  try {
    if (typeof window === 'undefined') return fallback;
    return window.localStorage.getItem(key);
  } catch (e) {
    return fallback;
  }
};

const safeLSSet = (key, val) => {
  try { localStorage.setItem(key, val); } catch (e) {}
};

/* ─────────────────────────────
   INIT STATE (NO CRASH)
───────────────────────────── */
const _savedCart = safeJSON(safeLSGet('cart', null), {});
// Strip organicSelected so toggles always start fresh on reload
Object.values(_savedCart).forEach(item => { delete item.organicSelected; });
// Recalculate totals from saved cart (localStorage values may be stale)
const _savedTotalItems = Object.keys(_savedCart).length;
const _savedTotalPrice = Object.values(_savedCart).reduce((s, p) => s + (p.price||0) * (p.quantity||0), 0);

const initState = {
  products: [],
  cart: _savedCart,
  totalItems: _savedTotalItems,
  totalPrice: _savedTotalPrice,
  searchTerm: '',
  showSearch: true,
  category: 'all',
  favorites: safeJSON(safeLSGet('favorites', null), [])
};

/* ─────────────────────────────
   REDUCER (PURE SAFE)
───────────────────────────── */
const cartReducer = (state = initState, action) => {
  let currCart, productID, newTotalItems, newTotalPrice;

  switch (action.type) {

    case FETCH_PRODUCTS:
      return { ...state, products: action.payload };

    case ADD_TO_CART: {
      const addedProduct = action.product;
      currCart = { ...state.cart };
      productID = addedProduct.id;

      if (!currCart[productID]) {
        currCart[productID] = { ...addedProduct, quantity: 0 };
      }

      const maxQty = 99;
      if (currCart[productID].quantity >= maxQty) return state;

      currCart[productID].quantity += 1;

      newTotalPrice = Object.values(currCart)
        .reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 0), 0);

      newTotalItems = Object.keys(currCart).length;

      safeLSSet('cart', JSON.stringify(currCart));
      return {
        ...state,
        cart: currCart,
        totalItems: newTotalItems,
        totalPrice: newTotalPrice
      };
    }

    case REMOVE_FROM_CART: {
      currCart = { ...state.cart };
      productID = action.product.id;

      if (currCart[productID]) {
        currCart[productID].quantity -= 1;
        if (currCart[productID].quantity <= 0) {
          delete currCart[productID];
        }
      }

      newTotalPrice = Object.values(currCart)
        .reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 0), 0);

      newTotalItems = Object.keys(currCart).length;

      safeLSSet('cart', JSON.stringify(currCart));
      return {
        ...state,
        cart: currCart,
        totalItems: newTotalItems,
        totalPrice: newTotalPrice
      };
    }

    case 'DELETE_FROM_CART': {
      currCart = { ...state.cart };
      productID = action.product.id;

      delete currCart[productID];

      newTotalPrice = Object.values(currCart)
        .reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 0), 0);

      newTotalItems = Object.keys(currCart).length;

      safeLSSet('cart', JSON.stringify(currCart));
      return {
        ...state,
        cart: currCart,
        totalItems: newTotalItems,
        totalPrice: newTotalPrice
      };
    }

    case CLEAR_CART:
      safeLSSet('cart', JSON.stringify({}));
      try { localStorage.removeItem('bv_cart_organic'); } catch(e) {}
      return {
        ...state,
        cart: {},
        totalItems: 0,
        totalPrice: 0
      };

    case SEARCH_PRODUCTS:
      return { ...state, searchTerm: action.term };

    case SHOW_SEARCH:
      return { ...state, showSearch: action.value };

    case CHANGE_CATEGORY:
      return { ...state, category: action.value };

    case TOGGLE_FAVORITE: {
      let newFavorites;

      if (action.payload.likedIds) {
        newFavorites = action.payload.likedIds;
      } else if (action.payload.toggleId) {
        const id = action.payload.toggleId;
        const exists = state.favorites.includes(id);

        newFavorites = exists
          ? state.favorites.filter(f => f !== id)
          : [...state.favorites, id];
      } else {
        return state;
      }

      // Зберігаємо в localStorage щоб пережити перезавантаження
      safeLSSet('favorites', JSON.stringify(newFavorites));

      return { ...state, favorites: newFavorites };
    }

    case UPDATE_CART_ITEM: {
      currCart = { ...state.cart };

      if (currCart[action.id]) {
        currCart[action.id] = {
          ...currCart[action.id],
          ...action.patch
        };

        newTotalPrice = Object.values(currCart)
          .reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 0), 0);

        newTotalItems = Object.keys(currCart).length;

        safeLSSet('cart', JSON.stringify(currCart));
        return {
          ...state,
          cart: currCart,
          totalItems: newTotalItems,
          totalPrice: newTotalPrice
        };
      }

      return state;
    }

    default:
      return state;
  }
};

export default cartReducer;
